<div class="use-avoir-dialog">
  <div class="dialog-header">
    <div class="header-title">
      <mat-icon class="header-icon">receipt_long</mat-icon>
      <div>
        <h2>Imputer un avoir</h2>
        <p class="subtitle">
          Reste à régler sur le document : <strong>{{ data.remainder | fc }}&nbsp;€</strong>
          @if (data.customerName) {
            <span>&nbsp;• Client : {{ data.customerName }}</span>
          }
        </p>
      </div>
    </div>
    <button mat-icon-button type="button" (click)="onCancel()" aria-label="Fermer">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <mat-dialog-content class="dialog-body">
    <!-- Zone de scan QR-code / recherche par référence -->
    <div class="search-section">
      <label class="section-label" for="avoirSearchInput">
        Scanner le QR-code du bon d'avoir ou saisir sa référence
      </label>
      <div class="search-bar">
        <div class="search-input-wrap">
          <mat-icon class="search-prefix-icon">qr_code_scanner</mat-icon>
          <input
            id="avoirSearchInput"
            type="text"
            class="search-input"
            [formControl]="searchControl"
            (keydown)="onSearchKeydown($event)"
            placeholder="Ex : AV-2026-12 ou scan du QR-code..."
            autocomplete="off"
          />
          @if (searchControl.value) {
            <button type="button" class="clear-btn" (click)="onClearSearch()" matTooltip="Effacer la recherche">
              <mat-icon>clear</mat-icon>
            </button>
          }
        </div>
        <button
          type="button"
          class="custom-filled-button primary search-btn"
          [disabled]="isLoading"
          (click)="onSearch()"
        >
          <mat-icon>search</mat-icon>
          Rechercher
        </button>
      </div>
      @if (searchError) {
        <div class="search-error">
          <mat-icon>warning</mat-icon>
          <span>{{ searchError }}</span>
        </div>
      }
    </div>

    <!-- Liste des avoirs disponibles -->
    <div class="list-section">
      <div class="list-header">
        <span class="section-label">Avoirs disponibles ({{ availableAvoirs.length }})</span>
      </div>

      @if (isLoading) {
        <div class="loading-box">
          <mat-spinner diameter="28"></mat-spinner>
          <span>Recherche des avoirs disponibles…</span>
        </div>
      } @else if (availableAvoirs.length === 0) {
        <div class="empty-box">
          <mat-icon>inbox</mat-icon>
          <span>Aucun avoir ouvert trouvé. Scannez un QR-code ou saisissez une référence AV-YYYY-XXX ci-dessus.</span>
        </div>
      } @else {
        <div class="avoir-table-container">
          <table class="avoir-table">
            <thead>
              <tr>
                <th>Référence</th>
                <th>Date</th>
                <th>Client</th>
                <th class="num">Initial</th>
                <th class="num">Déjà utilisé</th>
                <th class="num">Disponible</th>
              </tr>
            </thead>
            <tbody>
              @for (item of availableAvoirs; track item.avoir_order_id) {
                <tr
                  class="avoir-row"
                  [class.selected]="selectedAvoir?.avoir_order_id === item.avoir_order_id"
                  (click)="selectAvoir(item)"
                >
                  <td class="ref-cell">
                    <mat-icon class="radio-icon">
                      {{ selectedAvoir?.avoir_order_id === item.avoir_order_id ? 'radio_button_checked' : 'radio_button_unchecked' }}
                    </mat-icon>
                    <span class="ref-badge">{{ item.doc_ref }}</span>
                  </td>
                  <td>{{ item.issue_date }}</td>
                  <td>{{ item.customer_name }}</td>
                  <td class="num">{{ item.initial_amount | fc }}&nbsp;€</td>
                  <td class="num">{{ item.consumed_amount | fc }}&nbsp;€</td>
                  <td class="num available-cell">
                    <strong>{{ item.available_amount | fc }}&nbsp;€</strong>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </div>

    <!-- Paramètres d'imputation de l'avoir sélectionné -->
    @if (selectedAvoir; as sel) {
      <div class="imputation-section" [formGroup]="formGroup">
        <div class="selected-banner">
          <div>
            Avoir sélectionné : <strong>{{ sel.doc_ref }}</strong> ({{ sel.customer_name }})
          </div>
          <div class="avail-pill">
            Crédit disponible : <strong>{{ sel.available_amount | fc }}&nbsp;€</strong>
          </div>
        </div>

        <div class="imputation-inputs">
          <div class="field-group amount-group">
            <label for="avoirAmountInput">Montant à imputer (€ TTC)</label>
            <div class="amount-row">
              <input
                id="avoirAmountInput"
                type="number"
                step="0.01"
                min="0.01"
                [max]="maxImputableAmount"
                formControlName="amount"
                class="amount-input"
              />
              <button
                type="button"
                class="custom-filled-button secondary max-btn"
                matTooltip="Imputer le maximum possible"
                (click)="setFullMaxAmount()"
              >
                Max ({{ maxImputableAmount | fc }}&nbsp;€)
              </button>
            </div>
          </div>

          <div class="field-group comment-group">
            <label for="avoirCommentInput">Commentaire (optionnel)</label>
            <input
              id="avoirCommentInput"
              type="text"
              formControlName="comment"
              class="comment-input"
              [placeholder]="'Imputation avoir ' + sel.doc_ref"
            />
          </div>
        </div>

        <div class="summary-grid">
          <div class="summary-item">
            <span class="label">Solde restant sur l'avoir {{ sel.doc_ref }} après imputation :</span>
            <span class="value" [class.zero]="remainingOnAvoirAfter === 0">
              {{ remainingOnAvoirAfter | fc }}&nbsp;€
              @if (remainingOnAvoirAfter === 0) {
                <span class="status-tag">Soldé</span>
              } @else {
                <span class="status-tag partial">Partiel</span>
              }
            </span>
          </div>
          <div class="summary-item">
            <span class="label">Reste à régler sur la commande après imputation :</span>
            <span class="value" [class.zero]="remainingOnOrderAfter === 0">
              {{ remainingOnOrderAfter | fc }}&nbsp;€
            </span>
          </div>
        </div>
      </div>
    }
  </mat-dialog-content>

  <mat-dialog-actions align="end" class="dialog-actions">
    <button type="button" class="custom-filled-button secondary" [disabled]="isSubmitting" (click)="onCancel()">
      Annuler
    </button>
    <button
      type="button"
      id="confirmApplyAvoirBtn"
      class="custom-filled-button success"
      [disabled]="isSubmitting || !selectedAvoir || formGroup.invalid || currentAmount <= 0 || currentAmount > maxImputableAmount"
      (click)="onSubmit()"
    >
      <mat-icon>check_circle</mat-icon>
      Imputer l'avoir
      @if (selectedAvoir && currentAmount > 0) {
        <span>({{ currentAmount | fc }}&nbsp;€)</span>
      }
    </button>
  </mat-dialog-actions>
</div>
